import { g } from '@hcie/core';

/**
 * @file red_eye.ts
 * @description Red Eye Removal Tool implementation.
 * Processes an area under the brush to detect and diminish "redness" in eyes.
 */

/**
 * Removes red-eye effect by processing pixels in a circular area with feathering/hardness.
 * @param sourceCtx - The target canvas context to read from (active layer ctx)
 * @param destCtx - The target canvas context to write to (tempCtx)
 * @param x - Center X coordinate
 * @param y - Center Y coordinate
 * @param radius - Brush radius
 * @param hardness - Brush hardness (0.0 to 1.0)
 */
export function drawRedEye(sourceCtx: CanvasRenderingContext2D, destCtx: CanvasRenderingContext2D, x: number, y: number, radius: number, hardness: number = 0.8): void {
    if (!destCtx) return;
    const size = Math.ceil(radius * 2);
    const startX = Math.floor(x - radius);
    const startY = Math.floor(y - radius);
    
    try {
        const imageData = sourceCtx.getImageData(startX, startY, size, size);
        const data = imageData.data;
        
        // We read from source, but write to a buffer representing the dest
        const destImageData = destCtx.getImageData(startX, startY, size, size);
        const destData = destImageData.data;

        let modified = false;

        for (let i = 0; i < data.length; i += 4) {
            const px = (i / 4) % size;
            const py = Math.floor((i / 4) / size);
            
            const dx = px - radius;
            const dy = py - radius;
            const distSq = dx * dx + dy * dy;
            const radSq = radius * radius;
            
            if (distSq > radSq) continue;

            const dist = Math.sqrt(distSq);
            let weight = 1.0;
            if (hardness < 1.0) {
                const featherStart = radius * hardness;
                if (dist > featherStart) {
                    weight = 1.0 - (dist - featherStart) / (radius - featherStart);
                }
            }
            if (weight <= 0) continue;

            const r = data[i];
            const g_val = data[i + 1];
            const b = data[i + 2];
            const a = data[i + 3];

            if (a === 0) continue;

            const avgGB = (g_val + b) / 2;
            const redness = r / Math.max(1, avgGB);

            if (redness > 1.5 && r > 64) {
                const targetR = avgGB;
                const newR = r + (targetR - r) * weight;
                
                destData[i] = newR;
                destData[i + 1] = g_val;
                destData[i + 2] = b;
                destData[i + 3] = a; // Full target alpha so it overwrites source composite
                modified = true;
            }
        }

        if (modified) {
            destCtx.putImageData(destImageData, startX, startY);
        }
    } catch (err) {
        // Silently fail if outside canvas bounds, which is expected during stroke
    }
}

// Global exposure for legacy interop
(window as any).drawRedEye = drawRedEye;
